import { ChangeDetectorRef, Component, Input, OnChanges, Optional, Self, inject } from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { TranslationSandbox } from '@app/core/sandboxes/translation.sandbox';
import { DeviceType } from '@app/core/types/common/device.types';
import { SharedModule } from '@app/shared/shared.module';
import { ButtonComponent } from '@app/shared/ui/button/button.component';
import { Capacitor } from '@capacitor/core';
import { faCalendar, faChevronDown, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { MaskitoDirective } from '@maskito/angular';
import { MaskitoElementPredicate, MaskitoOptions } from '@maskito/core';
import { maskitoDateOptionsGenerator } from '@maskito/kit';
import { locale, MbscModule } from '@mobiscroll/angular';
import { DateTime } from 'luxon';

const DISPLAY_FORMAT = 'dd/MM/yyyy';

@Component({
  selector: 'c-date-picker',
  imports: [SharedModule, TranslocoPipe, MbscModule, ButtonComponent, MaskitoDirective],
  templateUrl: './date-picker.component.html',
  styleUrl: './date-picker.component.scss',
})
export class DatePickerComponent implements ControlValueAccessor, OnChanges {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translationSandbox = inject(TranslationSandbox);

  @Input() label: string;
  @Input() placeholder: string;
  @Input() helpText: string | undefined;
  @Input() minDate: string;
  @Input() maxDate = '2099-12-31';
  @Input() showError: boolean;
  @Input() showClearButton: boolean = false;

  faCalendar = faCalendar;
  faChevronDown = faChevronDown;
  faXmark = faXmark;
  isPickerOpen = false;

  readonly localeAll = locale;
  readonly currentLocale = this.translationSandbox.currentLocale;

  datePickerValue: Date;

  // Text shown in the field; differs from the control value while a date is being typed.
  inputText = '';
  maskitoOptions: MaskitoOptions = this.createMask();
  readonly maskitoPredicate: MaskitoElementPredicate = async (el) => ((el as unknown) as HTMLIonInputElement).getInputElement();

  onChange: (value: string | null) => void = () => {};
  onTouched = () => {};

  get control() {
    return this.ngControl?.control as FormControl;
  }

  constructor(@Optional() @Self() public ngControl: NgControl) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnChanges(): void {
    this.maskitoOptions = this.createMask();
  }

  setPickerOpen(open: boolean): void {
    this.isPickerOpen = open;
    this.cdr.detectChanges();
  }

  // Android only picks up the initial value once the modal has been presented; every other platform
  // (iOS and web) sets it before, so the calendar opens on the field's date instead of today.
  willPresentHandler(): void {
    if (Capacitor.getPlatform() !== DeviceType.Android) {
      this.setDefaultValue();
    }
  }

  didPresentHandler(): void {
    if (Capacitor.getPlatform() === DeviceType.Android) {
      this.setDefaultValue();
    }
  }

  writeValue(value: unknown): void {
    this.inputText = this.toDisplayText(value);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onInputChange(text: string): void {
    this.inputText = text;
    const date = DateTime.fromFormat(text, DISPLAY_FORMAT);
    // Incomplete or cleared input leaves the control empty until a full date is typed.
    this.onChange(text.length === DISPLAY_FORMAT.length && date.isValid ? date.toISODate() : null);
  }

  setDefaultValue(): void {
    const value = this.control.value;
    const date = value instanceof Date ? DateTime.fromJSDate(value) : DateTime.fromISO(String(value ?? ''));
    // A local Date, so an ISO date-only value isn't read as UTC midnight (the previous day in some timezones).
    this.datePickerValue = date.isValid ? date.toJSDate() : undefined;
  }

  cancelDatePicker(): void {
    this.clearSelectedDate();
    this.setPickerOpen(false);
  }

  clearControlValue(): void {
    this.control.setValue(null);
  }

  clearSelectedDate(): void {
    this.setDefaultValue();
  }

  confirmSelectedDate(): void {
    this.control.setValue(
      DateTime.fromJSDate(this.datePickerValue).toISODate()
    );
    this.setPickerOpen(false);
  }

  private toDisplayText(value: unknown): string {
    if (!value) {
      return '';
    }
    const date = value instanceof Date ? DateTime.fromJSDate(value) : DateTime.fromISO(String(value));
    return date.isValid ? date.toFormat(DISPLAY_FORMAT) : '';
  }

  private createMask(): MaskitoOptions {
    return maskitoDateOptionsGenerator({
      mode: 'dd/mm/yyyy',
      separator: '/',
      min: this.minDate ? DateTime.fromISO(this.minDate).toJSDate() : undefined,
      max: this.maxDate ? DateTime.fromISO(this.maxDate).toJSDate() : undefined,
    });
  }
}
